import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { creditos, gramos, iso } from "../lib/formato";
import { Cabecera, Cargando, FotoSocio, Panel, TarjetaDato, Vacio } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface Actividad {
  serie: { periodo: string; centimos: number; mg: number; tickets: number; socios: number }[];
  totales: { centimos: number; mg: number; tickets: number };
}

interface ProductoInforme {
  id: string;
  nombre: string;
  categoria: string;
  color: string;
  mg: number;
  centimos: number;
  veces: number;
}

interface SocioInforme {
  socio?: { id: string; numeroSocio: number; nombre: string; apellidos: string };
  mg: number;
  centimos: number;
  visitas: number;
}

interface Aportante {
  socio: {
    id: string;
    numeroSocio: number;
    nombre: string;
    apellidos: string;
    fotoUrl: string | null;
    estado: string;
    tipoSocio: { nombre: string; color: string } | null;
  } | null;
  dispensadoCentimos: number;
  dispensadoMg: number;
  visitas: number;
  barraCentimos: number;
  cuotasCentimos: number;
  donadoCentimos: number;
  totalCentimos: number;
}

interface Economico {
  aportaciones: { centimos: number; tickets: number };
  cuotas: { centimos: number; numero: number };
  recargasCartera: number;
  gastos: number;
  porMetodoPago: Record<string, number>;
}

export default function Informes() {
  const { t, cuenta } = useIdioma();
  const hace30 = new Date(Date.now() - 29 * 24 * 3600 * 1000);
  const [desde, setDesde] = useState(iso(hace30));
  const [hasta, setHasta] = useState(iso(new Date()));
  const [agrupar, setAgrupar] = useState<"dia" | "mes">("dia");

  const rango = { desde, hasta };
  const actividad = useQuery({
    queryKey: ["informe-actividad", desde, hasta, agrupar],
    queryFn: () => api<Actividad>("/informes/actividad", { query: { ...rango, agrupar } }),
  });
  const productos = useQuery({
    queryKey: ["informe-productos", desde, hasta],
    queryFn: () => api<ProductoInforme[]>("/informes/productos", { query: rango }),
  });
  const socios = useQuery({
    queryKey: ["informe-socios", desde, hasta],
    queryFn: () => api<SocioInforme[]>("/informes/socios", { query: rango }),
  });

  const aportan = useQuery({
    queryKey: ["informe-aportan", rango],
    queryFn: () => api<Aportante[]>("/informes/socios/aportan", { query: rango }),
  });
  const valoraciones = useQuery({
    queryKey: ["valoraciones"],
    queryFn: () =>
      api<{
        productos: { productoId: string; nombre: string; media: number; votos: number }[];
        comentarios: {
          id: string;
          puntuacion: number;
          comentario: string | null;
          producto: { nombre: string };
          socio: { numeroSocio: number; nombre: string; apellidos: string };
        }[];
      }>("/informes/valoraciones"),
  });

  const economico = useQuery({
    queryKey: ["informe-economico", desde, hasta],
    queryFn: () => api<Economico>("/informes/economico", { query: rango }),
  });

  const maximo = Math.max(1, ...(actividad.data?.serie.map((p) => p.mg) ?? [1]));

  return (
    <>
      <Cabecera titulo={t("in.titulo")} descripcion={t("in.descripcion")}>
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={{ width: 165 }} />
        <span className="suave">{t("in.a")}</span>
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={{ width: 165 }} />
        <select value={agrupar} onChange={(e) => setAgrupar(e.target.value as "dia" | "mes")} style={{ width: 130 }}>
          <option value="dia">{t("in.porDia")}</option>
          <option value="mes">{t("in.porMes")}</option>
        </select>
      </Cabecera>

      <div className="rejilla cuatro" style={{ marginBottom: 16 }}>
        <TarjetaDato
          etiqueta={t("in.aportaciones")}
          valor={economico.data ? creditos(economico.data.aportaciones.centimos) : "—"}
          pie={
            economico.data
              ? cuenta(economico.data.aportaciones.tickets, "in.unaEntrega", "in.entregas")
              : undefined
          }
        />
        <TarjetaDato
          etiqueta={t("in.dispensado")}
          valor={actividad.data ? gramos(actividad.data.totales.mg, 0) : "—"}
        />
        <TarjetaDato
          etiqueta={t("in.cuotasCobradas")}
          valor={economico.data ? creditos(economico.data.cuotas.centimos) : "—"}
          pie={economico.data ? cuenta(economico.data.cuotas.numero, "in.unaCuota", "in.cuotas") : undefined}
        />
        <TarjetaDato
          etiqueta={t("in.gastos")}
          valor={economico.data ? creditos(economico.data.gastos) : "—"}
          pie={<Link to="/gastos">{t("in.verDesglose")}</Link>}
        />
      </div>

      <Panel titulo={t("in.evolucion")}>
        {actividad.isLoading ? (
          <Cargando />
        ) : !actividad.data || actividad.data.serie.length === 0 ? (
          <Vacio>{t("in.sinActividad")}</Vacio>
        ) : (
          <>
            <div className="grafico">
              {actividad.data.serie.map((punto) => (
                <div key={punto.periodo} className="columna-barra" title={`${punto.periodo}: ${gramos(punto.mg)} · ${creditos(punto.centimos)}`}>
                  <div className="barra-vertical" style={{ height: `${(punto.mg / maximo) * 100}%` }} />
                  <span className="etiqueta-barra">
                    {agrupar === "dia" ? punto.periodo.slice(8) : punto.periodo.slice(5)}
                  </span>
                </div>
              ))}
            </div>
            <div className="fila separada pequeno suave" style={{ marginTop: 10 }}>
              <span>{cuenta(actividad.data.serie.length, "in.unPeriodo", "in.periodos")}</span>
              <span>
                {gramos(actividad.data.totales.mg)} · {creditos(actividad.data.totales.centimos)} ·{" "}
                {cuenta(actividad.data.totales.tickets, "in.unaEntrega", "in.entregas")}
              </span>
            </div>
          </>
        )}
      </Panel>

      <div className="rejilla dos" style={{ marginTop: 16 }}>
        <Panel titulo={t("in.masDispensados")} sinCuerpo>
          {productos.isLoading ? (
            <Cargando />
          ) : !productos.data || productos.data.length === 0 ? (
            <Vacio>{t("in.sinDatos")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("in.producto")}</th>
                    <th className="num">{t("in.cantidad")}</th>
                    <th className="num">{t("in.aportaciones")}</th>
                  </tr>
                </thead>
                <tbody>
                  {productos.data.slice(0, 12).map((p) => (
                    <tr key={p.id}>
                      <td>
                        <strong>{p.nombre}</strong>
                        <div>
                          <span className="etiqueta" style={{ background: `${p.color}20`, color: p.color }}>
                            {p.categoria}
                          </span>
                        </div>
                      </td>
                      <td className="num tabular">{gramos(p.mg)}</td>
                      <td className="num tabular">{creditos(p.centimos)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel titulo={t("in.masActivos")} sinCuerpo>
          {socios.isLoading ? (
            <Cargando />
          ) : !socios.data || socios.data.length === 0 ? (
            <Vacio>{t("in.sinDatos")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("in.socio")}</th>
                    <th className="num">{t("in.visitas")}</th>
                    <th className="num">{t("in.cantidad")}</th>
                  </tr>
                </thead>
                <tbody>
                  {socios.data.slice(0, 12).map((s, indice) => (
                    <tr key={s.socio?.id ?? indice}>
                      <td>
                        {s.socio ? (
                          <Link to={`/socios/${s.socio.id}`}>
                            #{s.socio.numeroSocio} {s.socio.nombre} {s.socio.apellidos}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="num tabular">{s.visitas}</td>
                      <td className="num tabular">{gramos(s.mg)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {/*
        * Quien mas aporta, en dinero. Es otra pregunta que la de arriba: el que
        * mas retira no tiene por que ser el que mas deja.
        */}
      <div style={{ marginTop: 16 }}>
        <Panel titulo={t("in.queMasAportan")}>
          {aportan.isLoading ? (
            <Cargando />
          ) : (aportan.data ?? []).length === 0 ? (
            <Vacio>{t("in.nadieHaAportado")}</Vacio>
          ) : (
            <>
              <ol className="aportantes">
                {(aportan.data ?? []).map((a, i) => (
                  <li key={a.socio!.id}>
                    <span className="puesto">{i + 1}</span>
                    <FotoSocio socio={a.socio!} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Link to={`/socios/${a.socio!.id}`}>
                        <strong>
                          {a.socio!.nombre} {a.socio!.apellidos}
                        </strong>
                      </Link>
                      <div className="suave pequeno">
                        #{a.socio!.numeroSocio}
                        {a.socio!.tipoSocio && ` · ${a.socio!.tipoSocio.nombre}`}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <strong className="tabular">{creditos(a.totalCentimos)}</strong>
                      <div className="suave pequeno">
                        {[
                          a.dispensadoCentimos > 0 &&
                            t("in.enGenero", { importe: creditos(a.dispensadoCentimos) }),
                          a.barraCentimos > 0 && t("in.enBarra", { importe: creditos(a.barraCentimos) }),
                          a.cuotasCentimos > 0 && t("in.deCuota", { importe: creditos(a.cuotasCentimos) }),
                          a.donadoCentimos > 0 && t("in.donado", { importe: creditos(a.donadoCentimos) }),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
              <p className="suave pequeno" style={{ marginBottom: 0 }}>
                {t("in.noCuentaCartera")}
              </p>
            </>
          )}
        </Panel>
      </div>

      {economico.data && (
        <div style={{ marginTop: 16 }}>
          <Panel titulo={t("in.porFormaDePago")}>
            <div className="rejilla tres">
              {Object.entries(economico.data.porMetodoPago).map(([metodo, centimos]) => (
                <div key={metodo}>
                  <div className="rotulo">{t(`pago.${metodo}` as Clave)}</div>
                  <div className="valor" style={{ fontSize: "1.35rem" }}>
                    {creditos(centimos)}
                  </div>
                </div>
              ))}
              <div>
                <div className="rotulo">{t("in.recargasCartera")}</div>
                <div className="valor" style={{ fontSize: "1.35rem" }}>
                  {creditos(economico.data.recargasCartera)}
                </div>
              </div>
            </div>
          </Panel>
        </div>
      )}
      {valoraciones.data && valoraciones.data.productos.length > 0 && (
        <div className="rejilla dos" style={{ marginTop: 16 }}>
          <Panel titulo={t("in.queOpinan")} sinCuerpo>
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("in.producto")}</th>
                    <th className="num">{t("in.nota")}</th>
                    <th className="num">{t("in.votos")}</th>
                  </tr>
                </thead>
                <tbody>
                  {valoraciones.data.productos.map((p) => (
                    <tr key={p.productoId}>
                      <td>{p.nombre}</td>
                      <td className="num tabular">
                        <strong>{p.media.toFixed(1).replace(".", ",")}</strong>
                        <span className="suave"> / 5</span>
                      </td>
                      {/* Una media de 5 con un voto no dice lo mismo que una
                          de 4,2 con treinta: por eso van juntos. */}
                      <td className="num tabular suave">{p.votos}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel titulo={t("in.queDicen")}>
            {valoraciones.data.comentarios.length === 0 ? (
              <p className="suave" style={{ margin: 0 }}>
                {t("in.nadieHaEscrito")}
              </p>
            ) : (
              <div className="columna" style={{ gap: 12 }}>
                {valoraciones.data.comentarios.map((c) => (
                  <div key={c.id}>
                    <div className="fila separada">
                      <strong>{c.producto.nombre}</strong>
                      <span style={{ color: "#e0a30b" }}>{"★".repeat(c.puntuacion)}</span>
                    </div>
                    <p style={{ margin: "2px 0" }}>{c.comentario}</p>
                    <span className="suave pequeno">
                      #{c.socio.numeroSocio} {c.socio.nombre} {c.socio.apellidos}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}
    </>
  );
}
